import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { GeologicalLoad } from 'src/features/well-form/entities/geological-load/geological-load.entity';
import type { GeologicalLoadViewModel } from 'src/features/well-form/geological-load-tab/geological-load-view-model';
import { Switcher } from 'src/shared/components/switcher/switcher';

import styles from './geological-load-item.module.scss';

type Props = {
  geoLoad: GeologicalLoad;
  geologicalLoadViewModel: GeologicalLoadViewModel;
};

export const GeologicalLoadItem = observer<Props>(function GeologicalLoadItem({ geoLoad, geologicalLoadViewModel }) {
  const { t } = useTranslation();

  const {
    coreSelection,
    testingFormationWork,
    dataInterpretation,
    coreSamplingIntervalId,
    testingFormationWorkAmount,
    numberOfDataInterpretationOperations,
    extractionVolume,
    coreSamplingDayAmount,
    testingFormationWorkDayAmount,
    daysAmountOfDataInterpretationOperation,
  } = geoLoad;

  const coreSamplingInterval = useMemo(() => {
    const directory = [
      ...geologicalLoadViewModel.coreSamplingIntervalOptions.actual,
      ...geologicalLoadViewModel.coreSamplingIntervalOptions.archived,
    ];

    if (!coreSamplingIntervalId.length) {
      return '-';
    }

    return coreSamplingIntervalId
      .map((value) => directory.find((v) => v.value === value)?.label ?? 'Нераспознанный интервал')
      .join(', ');
  }, [coreSamplingIntervalId, geologicalLoadViewModel.coreSamplingIntervalOptions]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.row}>
        <div className={styles.switcherItem}>
          <p className={styles.switcherText} title={t('geologicalLoad:labels.coreSelection')}>
            {t('geologicalLoad:labels.coreSelection')}
          </p>
          <Switcher isDisabled isActive={coreSelection} />
        </div>

        <div className={styles.readOnlyValueItem}>
          <p className={styles.labelText} title={t('geologicalLoad:labels.coreSamplingInterval')}>
            {t('geologicalLoad:labels.coreSamplingInterval')}
          </p>
          <p className={styles.readOnlyValueText}>{coreSamplingInterval}</p>
        </div>

        <div className={styles.readOnlyValueItem}>
          <p className={styles.labelText} title={t('geologicalLoad:labels.extractionVolume')}>
            {t('geologicalLoad:labels.extractionVolume')}
          </p>
          <p className={styles.readOnlyValueText}>{extractionVolume ?? '-'}</p>
        </div>

        <div className={styles.readOnlyValueItem}>
          <p className={styles.labelText} title={t('geologicalLoad:labels.coreSamplingDayAmount')}>
            {t('geologicalLoad:labels.coreSamplingDayAmount')}
          </p>
          <p className={styles.readOnlyValueText}>{coreSamplingDayAmount ?? '-'}</p>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.switcherItem}>
          <p className={styles.switcherText} title={t('geologicalLoad:labels.testingFormationWork')}>
            {t('geologicalLoad:labels.testingFormationWork')}
          </p>
          <Switcher isActive={testingFormationWork} isDisabled />
        </div>

        <div className={styles.readOnlyValueItem}>
          <p className={styles.labelText} title={t('geologicalLoad:labels.testingFormationWorkAmount')}>
            {t('geologicalLoad:labels.testingFormationWorkAmount')}
          </p>
          <p className={styles.readOnlyValueText}>{testingFormationWorkAmount ?? '-'}</p>
        </div>

        <div className={styles.readOnlyValueItem}>
          <p className={styles.labelText} title={t('geologicalLoad:labels.testingFormationWorkDayAmount')}>
            {t('geologicalLoad:labels.testingFormationWorkDayAmount')}
          </p>
          <p className={styles.readOnlyValueText}>{testingFormationWorkDayAmount ?? '-'}</p>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.switcherItem}>
          <p className={styles.switcherText} title={t('geologicalLoad:labels.dataInterpretation')}>
            {t('geologicalLoad:labels.dataInterpretation')}
          </p>
          <Switcher isActive={dataInterpretation} isDisabled />
        </div>

        <div className={styles.readOnlyValueItem}>
          <p className={styles.labelText} title={t('geologicalLoad:labels.numberOfDataInterpretationOperations')}>
            {t('geologicalLoad:labels.numberOfDataInterpretationOperations')}
          </p>
          <p className={styles.readOnlyValueText}>{numberOfDataInterpretationOperations ?? '-'}</p>
        </div>

        <div className={styles.readOnlyValueItem}>
          <p className={styles.labelText} title={t('geologicalLoad:labels.daysAmountOfDataInterpretationOperation')}>
            {t('geologicalLoad:labels.daysAmountOfDataInterpretationOperation')}
          </p>
          <p className={styles.readOnlyValueText}>{daysAmountOfDataInterpretationOperation ?? '-'}</p>
        </div>
      </div>
    </div>
  );
});
